import React from 'react'
import { Link } from '@inertiajs/react'
import { AlertTriangle, ArrowRight, ExternalLink } from 'lucide-react'
import DashboardCard from '@/components/dashboard/DashboardCard'
import type { MarketComparison, MarketFreshness, MarketListingStatus, MarketPositionRuleStatus } from '@/types/market'

/**
 * Whether to trust the page, in full: when prices were checked and who was missed, what exactly is
 * compared, your own listing, and whether the engine uses any of this (the Market Position rule).
 */

export const LIST_PRICE_LINE =
  'Competitor prices are list prices: the cheapest room for two, before member and deal discounts, taxes and fees included.'

function Row({ term, children }: { term: string; children: React.ReactNode }) {
  return (
    <div className="grid gap-1 py-3 sm:grid-cols-[180px_minmax(0,1fr)] sm:gap-4">
      <dt className="text-xs font-semibold text-foreground">{term}</dt>
      <dd className="space-y-1 text-sm text-muted-foreground">{children}</dd>
    </div>
  )
}

const linkClass =
  'inline-flex cursor-pointer items-center gap-1 rounded text-sm font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40'

export default function AboutThisData({
  freshness,
  comparison,
  listing,
  marketPositionRule,
  compsetHref,
  canManage,
}: {
  freshness: MarketFreshness | null
  comparison: MarketComparison
  listing: MarketListingStatus
  marketPositionRule: MarketPositionRuleStatus
  compsetHref: string
  canManage: boolean
}) {
  return (
    <section id="about-this-data" aria-labelledby="about-this-data-title" className="scroll-mt-6">
      <DashboardCard title={<span id="about-this-data-title">About this data</span>}>
        <dl className="divide-y divide-border/40 [&>div:first-child]:pt-0 [&>div:last-child]:pb-0">
          {freshness && (
            <>
              <Row term="Last checked">
                <p>{freshness.sentence}</p>
              </Row>
              <Row term="Next check">
                <p>{freshness.nextCheckLabel}</p>
              </Row>
              <Row term="Checked this time">
                <p className="tabular-nums">
                  {freshness.competitorsChecked} of {freshness.competitorsTotal} competitors
                </p>
                {freshness.missingCompetitors.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {freshness.missingCompetitors.map((name) => (
                      <span
                        key={name}
                        className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-800 ring-1 ring-inset ring-amber-200"
                      >
                        <AlertTriangle className="h-3 w-3 text-amber-600" />
                        {name}
                      </span>
                    ))}
                  </div>
                )}
              </Row>
            </>
          )}
          <Row term="What we compare">
            <p className="text-foreground">{comparison.sentence}</p>
            {comparison.detail && <p>{comparison.detail}</p>}
            <p>{LIST_PRICE_LINE}</p>
          </Row>
          <Row term="Your Booking.com listing">
            {listing.linked && listing.bookingComUrl ? (
              <a href={listing.bookingComUrl} target="_blank" rel="noopener noreferrer" className={linkClass}>
                Linked
                <ExternalLink className="h-3.5 w-3.5" />
                <span className="sr-only">(opens Booking.com in a new tab)</span>
              </a>
            ) : (
              <p className="flex flex-wrap items-center gap-x-2">
                <span className="text-foreground">Not linked</span>
                <Link href={compsetHref} className={linkClass}>
                  {canManage ? 'Link your listing' : 'Open Compset'}
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </p>
            )}
            {listing.sentence && <p>{listing.sentence}</p>}
          </Row>
          <Row term="Market Position rule">
            <p>{marketPositionRule.sentence}</p>
            <Link href={marketPositionRule.href} className={linkClass}>
              Open Market Position
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </Row>
        </dl>
      </DashboardCard>
    </section>
  )
}
